import { FC, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PaginationBar from '../components/PaginationBar';
import { statusBadgeCx } from '../utils/statusBadges';
import {
  fetchOrders,
  FetchOrdersParams,
  OrderResponse,
  OrderStatus,
} from '../api/orders';
import { fetchCurrentUser, MeResponse } from '../api/auth';
import { fetchCustomers, Customer } from '../api/customers';
import { fetchVehicleBrands, VehicleBrand } from '../api/vehicleBrands';
import { fetchVehicleModels, VehicleModel } from '../api/vehicleModels';

const STATUS_OPTIONS: { value: OrderStatus; label: string }[] = [
  { value: 'initiated', label: 'Initiated' },
  { value: 'draft', label: 'Draft' },
  { value: 'approved', label: 'Approved' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'completed', label: 'Completed' },
  { value: 'verified', label: 'Verified' },
  { value: 'done', label: 'Done' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'cancelled', label: 'Cancelled' },
];

const PAGE_SIZE = 10;

const labelCx = 'text-[11px] font-semibold uppercase tracking-wide text-slate-400';
const inputCx =
  'rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30 disabled:cursor-not-allowed disabled:bg-slate-50';

const OrderManagement: FC = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState<OrderResponse[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [me, setMe] = useState<MeResponse | null>(null);

  // Filters
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [customerIdFilter, setCustomerIdFilter] = useState<string>('');
  const [brandFilter, setBrandFilter] = useState<string>('');
  const [modelFilter, setModelFilter] = useState<string>('');
  const [createdFrom, setCreatedFrom] = useState('');
  const [createdTo, setCreatedTo] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Customers (async search + pick)
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [customerSearch, setCustomerSearch] = useState('');
  const [customersLoading, setCustomersLoading] = useState(false);

  // Vehicles
  const [vehicleBrands, setVehicleBrands] = useState<VehicleBrand[]>([]);
  const [vehicleModels, setVehicleModels] = useState<VehicleModel[]>([]);

  // Debounce top search
  useEffect(() => {
    const handle = window.setTimeout(() => {
      setDebouncedSearch(searchTerm.trim());
    }, searchTerm ? 350 : 0);
    return () => window.clearTimeout(handle);
  }, [searchTerm]);

  // Load current user (for location scoping)
  useEffect(() => {
    let mounted = true;
    fetchCurrentUser()
      .then((resp) => { if (mounted) setMe(resp); })
      .catch(() => { if (mounted) setMe(null); });
    return () => { mounted = false; };
  }, []);

  // Load orders
  useEffect(() => {
    let ignore = false;
    const loadOrders = async () => {
      setLoading(true);
      setError(null);

      const params: FetchOrdersParams = {
        page,
        limit: PAGE_SIZE,
      };
      if (statusFilter) params.status = statusFilter as OrderStatus;
      if (customerIdFilter) params.customerId = customerIdFilter;
      if (brandFilter) params.vehicleBrandId = brandFilter;
      if (modelFilter) params.vehicleModelId = modelFilter;
      if (createdFrom) {
        const startOfDay = new Date(createdFrom + 'T00:00:00');
        params.createdFrom = startOfDay.toISOString();
      }
      if (createdTo) {
        const endOfDay = new Date(createdTo + 'T23:59:59.999');
        params.createdTo = endOfDay.toISOString();
      }
      if (debouncedSearch) params.search = debouncedSearch;

      // Restrict by user locations when role is ADVISOR or STORE_MANAGER and locations array is non-empty
      const role = (me?.role ?? me?.user?.typeKey ?? '') as string;
      const userLocs = (me?.user as any)?.locations as Array<{ id: number; name: string }> | undefined;
      if ((role || '').toUpperCase() === 'ADVISOR' || (role || '').toUpperCase() === 'STORE_MANAGER') {
        if (Array.isArray(userLocs) && userLocs.length > 0) {
          params.locationErpIds = userLocs.map((l) => l.id);
        }
      }

      try {
        const response = await fetchOrders(params);
        if (ignore) return;
        setOrders(response.data);
        setTotal(response.meta.total);
        setTotalPages(response.meta.totalPages);
      } catch (fetchError) {
        if (!ignore) {
          setError(fetchError instanceof Error ? fetchError.message : 'Failed to load orders');
          setOrders([]);
          setTotal(0);
          setTotalPages(1);
        }
      } finally {
        if (!ignore) setLoading(false);
      }
    };
    void loadOrders();
    return () => {
      ignore = true;
    };
  }, [page, statusFilter, customerIdFilter, brandFilter, modelFilter, createdFrom, createdTo, debouncedSearch]);

  // Load brands
  useEffect(() => {
    let isCancelled = false;
    const loadBrands = async () => {
      try {
        const response = await fetchVehicleBrands({ limit: 100 });
        if (!isCancelled) setVehicleBrands(response.data);
      } catch (brandError) {
        if (!isCancelled) console.error('Failed to load vehicle brands', brandError);
      }
    };
    void loadBrands();
    return () => {
      isCancelled = true;
    };
  }, []);

  // Load models for selected brand (or all if brand empty, per API contract)
  useEffect(() => {
    let isCancelled = false;
    const loadModels = async () => {
      try {
        const response = await fetchVehicleModels({ limit: 100, brandId: brandFilter || undefined });
        if (!isCancelled) setVehicleModels(response.data);
      } catch (modelError) {
        if (!isCancelled) console.error('Failed to load vehicle models', modelError);
      }
    };
    void loadModels();
    return () => {
      isCancelled = true;
    };
  }, [brandFilter]);

  // Load customers (debounced search)
  useEffect(() => {
    let ignore = false;
    const timeoutId = window.setTimeout(async () => {
      setCustomersLoading(true);
      try {
        const response = await fetchCustomers({ limit: 20, search: customerSearch || undefined });
        if (!ignore) setCustomers(response.data);
      } catch (customerError) {
        if (!ignore) console.error('Failed to load customers', customerError);
      } finally {
        if (!ignore) setCustomersLoading(false);
      }
    }, customerSearch ? 350 : 0);
    return () => {
      ignore = true;
      window.clearTimeout(timeoutId);
    };
  }, [customerSearch]);

  // Reset all filters
  const handleResetFilters = () => {
    setStatusFilter('');
    setCustomerIdFilter('');
    setBrandFilter('');
    setModelFilter('');
    setCreatedFrom('');
    setCreatedTo('');
    setCustomerSearch('');
    setSearchTerm('');
    setDebouncedSearch('');
    setPage(1);
  };

  // When any filter changes, go to first page
  useEffect(() => {
    setPage(1);
  }, [statusFilter, customerIdFilter, brandFilter, modelFilter, createdFrom, createdTo, debouncedSearch]);

  // Clear model when brand cleared
  useEffect(() => {
    if (!brandFilter) setModelFilter('');
  }, [brandFilter]);

  const ordersSummary = useMemo(() => {
    if (!total) return 'No orders found';
    const start = (page - 1) * PAGE_SIZE + (orders.length ? 1 : 0);
    const end = (page - 1) * PAGE_SIZE + orders.length;
    return `Showing ${start}-${end} of ${total} orders`;
  }, [orders.length, page, total]);

  const renderCustomerLabel = (c: Customer) => {
    const name = `${c.firstName ?? ''} ${c.lastName ?? ''}`.trim();
    return name || c.email || c.mobileNumber || 'Unnamed customer';
  };

  const formatAmount = (value?: string | number | null) => {
    const numeric = Number(value ?? 0) || 0;
    return numeric.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  // description handled inline below

  return (
    <div className="mx-auto w-full max-w-[96rem] rounded-[28px] border border-slate-200/70 bg-white/95 p-10 shadow-[0_20px_45px_-32px_rgba(37,40,85,0.55)]">
      {/* Header */}
      <div className="flex flex-col gap-3 pb-0 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-3xl font-semibold text-slate-800">Order Management</h1>
          <p className="max-w-2xl text-sm text-slate-500">
            Track every workshop job from intake through delivery. Filter the queue to focus on specific customers,
            vehicle details, or time windows, then drill into an order to continue the workflow.
          </p>
        </div>

        {
          <button
            type="button"
            className="ml-auto rounded-md border border-slate-200 bg-primary px-4 py-2 text-sm font-semibold text-white shadow-sm"
            onClick={() => navigate('/order-management/new')}
          >
            + New order
          </button>
        }
      </div>

      {/* Content */}
      <div className="mt-2">
        <section className="rounded-2xl bg-white p-4 shadow-inner">
          {/* Redesigned Filters */}
          <div className="flex flex-col gap-3">
            {/* Top row: search + reset */}
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <input
                id="job-search"
                type="search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Order #, Job ID, customer, phone"
                className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm text-slate-600 shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30 sm:w-80"
              />
              {(statusFilter || customerIdFilter || brandFilter || modelFilter || createdFrom || createdTo) && (
                <button
                  type="button"
                  className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 transition hover:border-primary/30 hover:text-primary shadow-sm"
                  onClick={handleResetFilters}
                >
                  <span>✕</span> Clear Filter
                </button>
              )}
            </div>
            {/* Grid of individual filters */}
            <div className="w-full flex flex-wrap items-center gap-4">
              {/* Status */}
              <div className="flex flex-col gap-1 flex-1 min-w-[180px]">
                <label htmlFor="status-filter" className="sr-only">Status</label>
                <select
                  id="status-filter"
                  className={`${inputCx} h-11 text-base`} // slightly taller and bigger text
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="">All status</option>
                  {STATUS_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>

              {/* Brand */}
              <div className="flex flex-col gap-1 flex-1 min-w-[200px]">
                <label htmlFor="brand-filter" className="sr-only">Vehicle brand</label>
                <select
                  id="brand-filter"
                  className={`${inputCx} h-11 text-base`}
                  value={brandFilter}
                  onChange={(e) => {
                    setBrandFilter(e.target.value);
                    setModelFilter('');
                  }}
                >
                  <option value="">All brands</option>
                  {vehicleBrands.map((brand) => (
                    <option key={brand.id} value={brand.id}>{brand.name}</option>
                  ))}
                </select>
              </div>

              {/* Model */}
              <div className="flex flex-col gap-1 flex-1 min-w-[200px]">
                <label htmlFor="model-filter" className="sr-only">Vehicle model</label>
                <select
                  id="model-filter"
                  className={`${inputCx} h-11 text-base`}
                  value={modelFilter}
                  onChange={(e) => setModelFilter(e.target.value)}
                  disabled={!brandFilter}
                >
                  <option value="">{brandFilter ? 'All models' : 'All models'}</option>
                  {vehicleModels.map((model) => (
                    <option key={model.id} value={model.id}>{model.name}</option>
                  ))}
                </select>
              </div>

              {/* Date range */}
              <div className="flex flex-col gap-1 flex-[1.5] min-w-[250px]">
                <div className="grid grid-cols-2 gap-2">
                  <div className="flex flex-col gap-1">
                    <label htmlFor="created-from" className="sr-only">From</label>
                    <input
                      id="created-from"
                      type="date"
                      value={createdFrom}
                      onChange={(e) => setCreatedFrom(e.target.value)}
                      className={`${inputCx} h-11 text-base`}
                    />
                  </div>
                  <div className="flex flex-col gap-1">
                    <label htmlFor="created-to" className="sr-only">To</label>
                    <input
                      id="created-to"
                      type="date"
                      value={createdTo}
                      onChange={(e) => setCreatedTo(e.target.value)}
                      className={`${inputCx} h-11 text-base`}
                      min={createdFrom || undefined}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Helper tip */}
            <div className="flex flex-wrap items-center gap-2 pt-1 text-[11px] text-slate-400">
              <span>Tip: Type a phone or Job ID in search. Brand must be selected to enable model.</span>
            </div>
          </div>



          {/* Table */}
          <div className="mt-6 overflow-hidden rounded-2xl border border-slate-200">
            <table className="min-w-full text-left text-sm text-slate-600">
              <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-5 py-3 font-semibold">Order #</th>
                  <th className="px-5 py-3 font-semibold">Customer</th>
                  <th className="px-5 py-3 font-semibold">Vehicle</th>
                  <th className="px-5 py-3 font-semibold">Location</th>
                  <th className="px-5 py-3 font-semibold">Status</th>
                  <th className="px-5 py-3 font-semibold text-right">Total</th>
                  <th className="px-5 py-3 font-semibold">Created</th>
                  <th className="px-5 py-3 font-semibold">Updated</th>
                  <th className="px-5 py-3 font-semibold text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td className="px-5 py-6 text-center text-slate-400" colSpan={9}>
                      Loading orders…
                    </td>
                  </tr>
                ) : error ? (
                  <tr>
                    <td className="px-5 py-6 text-center text-danger" colSpan={9}>
                      {error}
                    </td>
                  </tr>
                ) : orders.length === 0 ? (
                  <tr>
                    <td className="px-5 py-6 text-center text-slate-400" colSpan={9}>
                      No orders match the selected filters.
                    </td>
                  </tr>
                ) : (
                  orders.map((order) => {
                    let customerName = 'Customer';
                    if (order.customer) {
                      const fullName = `${order.customer.firstName ?? ''} ${order.customer.lastName ?? ''}`.trim();
                      customerName = fullName || order.customer.email || order.customer.mobileNumber || 'Customer';
                    }

                    const vehicleLabel = order.customerVehicle
                      ? [
                        order.customerVehicle.vehicleBrand?.name,
                        order.customerVehicle.vehicleModel?.name,
                        order.customerVehicle.vehicleRegistrationNumber,
                      ]
                        .filter(Boolean)
                        .join(' · ')
                      : '—';

                    const statusLabel =
                      STATUS_OPTIONS.find((option) => option.value === order.status)?.label ?? order.status;

                    const canEdit = order.status !== 'done' && order.status !== 'delivered';
                    return (
                      <tr key={order.id} className="even:bg-slate-50/70">
                        <td className="px-5 py-3 font-semibold text-slate-700">{order.orderNumber}</td>
                        <td className="px-5 py-3 text-slate-600">{customerName}</td>
                        <td className="px-5 py-3 text-slate-600">{vehicleLabel}</td>
                        <td className="px-5 py-3 text-slate-600">{((order as any).location?.name as string) || order.locationCode || '—'}</td>
                        <td className="px-5 py-3">
                          <span className={statusBadgeCx(order.status)}>{statusLabel}</span>
                        </td>
                        <td className="px-5 py-3 text-right font-semibold text-slate-700">
                          {order.grandTotal ? formatAmount(order.grandTotal) : '—'}
                        </td>
                        <td className="px-5 py-3 text-slate-500">{new Date(order.createdAt).toLocaleString()}</td>
                        <td className="px-5 py-3 text-slate-500">{new Date(order.updatedAt).toLocaleString()}</td>
                        <td className="px-5 py-3 text-right">
                          <div className="inline-flex gap-2">
                            {canEdit ? (
                              <button
                                type="button"
                                className="inline-flex items-center justify-center rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 transition hover:border-primary/30 hover:text-primary"
                                onClick={() => navigate(`/order-management/new/customer-vehicle?orderId=${order.id}`)}
                              >
                                Edit
                              </button>
                            ) : null}
                            <button
                              type="button"
                              className="inline-flex items-center justify-center rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 transition hover:border-primary/30 hover:text-primary"
                              onClick={() => navigate(`/order-management/${order.id}`)}
                            >
                              View
                            </button>

                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="mt-4">
            <PaginationBar
              page={page}
              totalPages={totalPages}
              onPrev={() => setPage((current) => Math.max(1, current - 1))}
              onNext={() => setPage((current) => Math.min(totalPages, current + 1))}
              onSelect={(value) => setPage(value)}
              disabled={loading || totalPages === 0}
              renderSummary={<span>{ordersSummary}</span>}
            />
          </div>
        </section>
      </div>
    </div>
  );
};

export default OrderManagement;
